/**
 * What the watch on this company has found, on the company's own card.
 *
 * The counterpart to the "Watch this company" button: that one argues for a watch,
 * this one reports on the one that exists. The server sends whichever applies and
 * never both, so a reader is never choosing which to believe.
 *
 * DERIVED ON READ, and that is the whole reason job postings are not board items.
 * An item would change every table, every `missing` count and every hole the
 * director deals out to a persona — in a room nobody is looking at. A read changes
 * nothing, and goes stale the moment the watch moves on, which is correct.
 *
 * WORST THING FIRST. A stopped check says so before a working one reports how much
 * it is watching, the same ordering the Watch list itself uses: a monitor that has
 * quietly stopped must never read like one that is working and finding nothing.
 */
import { useLocation } from 'react-router-dom';
import ShellLink from '../common/ShellLink';
import { watchSubjectHref } from '../../pages/watchFeed';
import { tierTone } from '../watch/tierTone';
import type { WatchLive } from '../../types';

export default function WatchPanel({
  live,
  returnLabel,
}: {
  live: WatchLive;
  /** What to call this room on the way back. `returnPath.ts` is emphatic that a trail names the
   *  PAGE, not the app. */
  returnLabel?: string | null;
}) {
  const broken = live.paused.length > 0;
  // The exact url being read, so the way back lands on the profile you were on.
  const location = useLocation();

  return (
    <div className="space-y-1.5" data-testid={`watch-live-${live.subject_id}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <span
          className={`text-xs ${
            broken
              ? 'text-red-700 dark:text-red-300'
              : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          {live.headline}
        </span>
        <ShellLink
          to={watchSubjectHref(
            live.subject_id,
            `${location.pathname}${location.search}`,
            returnLabel,
          )}
          className="text-xs underline text-gray-500 dark:text-gray-400"
        >
          Open the watch
        </ShellLink>
      </div>

      {live.events.length > 0 ? (
        <ul className="space-y-1">
          {live.events.map((event) => {
            const tone = tierTone(event.tier);
            return (
              <li key={event.id} className="flex items-baseline gap-2 text-xs">
                <span
                  className={`shrink-0 px-1 rounded text-[11px] ${tone.chip}`}
                  title={tone.hint}
                >
                  {tone.label}
                </span>
                <span className="text-gray-700 dark:text-gray-200">{event.headline}</span>
                <span className="shrink-0 text-gray-400">
                  {event.detected_at ? new Date(event.detected_at).toLocaleDateString() : ''}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {/* Not "nothing to report": a first look records what is there now and reports nothing,
              so a new watch with an empty list is working correctly rather than idle. */}
          {live.awaiting_first_look > 0
            ? 'The first look records what is there now - changes come after that.'
            : 'Nothing has changed since we started watching.'}
        </p>
      )}
    </div>
  );
}
